import { useContext, useEffect, useState, useRef } from 'react';
import { endOfDay, startOfDay, isWithinInterval } from 'date-fns';
import { ctxPopup } from '@/contexts/PopupContext';
import { ctxTachographDiagram } from '@/contexts/TachographDiagramCtx';
import {
  tachograph,
  TachographDataDuringFromToPeriod,
  TachographDataInDay,
  TachographState,
} from '@/components/tachograph/diagram/TachographService';
import { day } from '@/utilities/timeUtils';

export function useTachographDataProcessing(): TachographDataDuringFromToPeriod[] | undefined {
  const ctx = useContext(ctxTachographDiagram);
  const popupCtx = useContext(ctxPopup);
  const [processedData, setProcessedData] = useState<TachographDataDuringFromToPeriod[] | undefined>(undefined);
  const timeoutId = useRef<NodeJS.Timeout | undefined>(undefined);

  useEffect(() => {
    if (!ctx || !ctx.selectedDates || !ctx.selectedTarget) {
      ctx?.setStatus('idle');
      return;
    }
    const controller = new AbortController();
    setProcessedData(undefined);
    async function loadTachData() {
      if (!ctx || !ctx.selectedTarget || !ctx.selectedDates || !ctx.selectedDates.from || !ctx.selectedDates.to) {
        return;
      }
      popupCtx?.showLoader();
      cancelRescheduleIfPresent();
      try {
        const tachdata = await tachograph(
          ctx.selectedTarget.id,
          day(ctx.selectedDates.from.getTime() * 1000),
          day(ctx.selectedDates.to.getTime() * 1000),
          controller.signal
        );
        convertingToCompactData(tachdata.data?.reverse());
      } catch (e) {
        console.log('error loading tachograph data', e);
        ctx.setStatus('empty');
      } finally {
        if (!controller.signal.aborted) {
          rescheduleTachoDataLoading();
        }
        popupCtx?.hideLoader();
      }
    }

    function includesToday() {
      if (!ctx || !ctx.selectedDates?.from || !ctx.selectedDates?.to) return false;
      const today = new Date();
      const interval = {
        start: startOfDay(ctx.selectedDates.from),
        end: endOfDay(ctx.selectedDates.to),
      };
      return isWithinInterval(today, interval);
    }

    function cancelRescheduleIfPresent() {
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
        timeoutId.current = undefined;
      }
    }

    function rescheduleTachoDataLoading() {
      if (!includesToday()) {
        return;
      }
      const id = setTimeout(async () => {
        await loadTachData();
      }, 60000);
      timeoutId.current = id;
    }

    function convertingToCompactData(serverData: TachographDataInDay[] | undefined) {
      if (!serverData || serverData.length === 0) {
        ctx?.setStatus('loading');
        return;
      }

      function isEmptyDay(data: TachographDataInDay) {
        function hasDifferentThanNoCard(state: TachographState): boolean {
          return state !== TachographState.NO_CARD;
        }

        const isThereDataForTheDay =
          data.states.find((stateWithTime) => hasDifferentThanNoCard(stateWithTime.state)) === undefined;

        return isThereDataForTheDay;
      }

      const emptyPeriod = (date: Date): TachographDataDuringFromToPeriod => ({
        fromDate: date,
        toDate: date,
      });

      const dayWithData = (d: TachographDataInDay): TachographDataDuringFromToPeriod => ({
        fromDate: new Date(d.date),
        toDate: new Date(d.date),
        states: d.states,
        summary: d.summary,
        borderCrossings: d.borderCrossings,
        driversName: d.driversName,
      });

      const processed: TachographDataDuringFromToPeriod[] = [];

      for (const day of serverData) {
        if (isEmptyDay(day)) {
          const last = processed[processed.length - 1];

          if (last && !last.states) {
            processed[processed.length - 1] = { ...last, toDate: new Date(day.date) };
          } else {
            processed.push(emptyPeriod(new Date(day.date)));
          }
        } else {
          processed.push(dayWithData(day));
        }
      }

      ctx?.setStatus('data');
      setProcessedData(processed);
    }
    ctx.setStatus('loading');
    void loadTachData();

    return () => {
      cancelRescheduleIfPresent();
      controller.abort();
    };
  }, [ctx?.selectedTarget, ctx?.selectedDates]);

  return processedData;
}
